/* Knobs.co — Cabinet Hardware Wizard quiz controller.
   ─────────────────────────────────────────────────────────────────
   Multi-step quiz state machine. Drives the quiz on /pages/wizard (or
   /pages/dev-cabinet-wizard-build while in development).

   Spec: Obsidian → Ecommerce/Knobs Migration/Cabinet Hardware Wizard - Question Tree v1.md
   Created: 2026-05-20 (task #3). Trade-application backend wiring lands
   in task #4; results-page filter composition lands in task #5.

   Architecture:
     STEPS[] — ordered step registry, each with id/type/title/options.
               Steps may carry a condition(state) predicate for skipping
               (e.g. q4_drill skipped if no pulls in q1_types).
     state.answers — keyed by step id; values are strings, arrays, or objects.
     effectiveSteps() — STEPS filtered by current conditions.
     navigate(delta) — moves through effective steps; validates on next().
     syncUrl() — mirrors answers into URL params so refresh/share works.
     restoreFromUrl() — reads URL on load to resume a partial session.

   Trade routing:
     q6_trade only personalizes the results page (Trade Pros program invite
     linking to /pages/trade). The in-quiz trade form was removed 2026-06-12.
*/
(function () {
  'use strict';

  var root = document.getElementById('knobs-wizard');
  if (!root) return;

  var hostEl = root.querySelector('[data-step-host]');
  var loadingEl = root.querySelector('[data-loading]');
  var progressEl = root.querySelector('[data-progress]');
  var progressBarEl = root.querySelector('[data-progress-bar]');

  var resultsUrl = root.getAttribute('data-results-url') || '/pages/wizard-results';

  /* ── Finish swatch colors (CSS, for Q3 cards) ───────────────────── */
  var FINISH_SWATCH = {
    'nickel':  '#A8A9AD',
    'black':   '#1A1A1A',
    'bronze':  '#5C4033',
    'brass':   '#B5A642',
    'chrome':  '#D0D0D2',
    'gold':    '#D4AF37',
    'iron':    '#3F3F3F',
    'copper':  '#B87333',
    'pewter':  '#8E9295',
    'gray':    '#808080',
    'steel':   '#71797E',
    'brown':   '#7B5E3B',
    'clear':   'linear-gradient(135deg,#f4f4f5 0%,#d6d6d8 50%,#f4f4f5 100%)',
    'aluminum':'#C0C0C0',
    'silver':  '#C0C0C0',
    'white':   '#FFFFFF',
    'colored': 'linear-gradient(45deg,#ff6b6b,#ffd93d,#6bcf7f,#4ecdc4,#95a5d8,#c779d0)'
  };

  /* ── Inline SVG icons for non-finish steps ──────────────────────── */
  function svg(name) {
    var paths = {
      knob:    '<circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2" fill="currentColor"/>',
      pull:    '<rect x="3" y="10" width="18" height="4" rx="2"/><circle cx="6" cy="12" r="1" fill="currentColor"/><circle cx="18" cy="12" r="1" fill="currentColor"/>',
      hinge:   '<path d="M9 4v16M15 4v16"/><circle cx="9" cy="8" r="1" fill="currentColor"/><circle cx="9" cy="16" r="1" fill="currentColor"/><circle cx="15" cy="8" r="1" fill="currentColor"/><circle cx="15" cy="16" r="1" fill="currentColor"/>',
      plate:   '<rect x="4" y="4" width="16" height="16" rx="2"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/>',
      box:     '<rect x="4" y="6" width="16" height="14" rx="1"/><path d="M4 10h16"/>',
      misc:    '<circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>',
      fast:    '<path d="M13 2L4 14h7l-1 8 9-12h-7z" fill="currentColor"/>',
      flexible:'<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
      custom:  '<path d="M7 21l5-3 5 3-2-6 5-4-6-1-2-6-2 6-6 1 5 4z" fill="currentColor"/>',
      home:    '<path d="M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z"/>',
      trade:   '<path d="M3 7l9-4 9 4v10l-9 4-9-4z"/><path d="M3 7l9 4 9-4M12 11v10"/>',
      check:   '<path d="M5 12l4 4 10-10"/>',
      maybe:   '<circle cx="12" cy="12" r="9"/><path d="M9 9c0-1.5 1.5-3 3-3s3 1.5 3 3-3 2-3 4M12 17v.5"/>',
      none:    '<circle cx="12" cy="12" r="9"/><path d="M5 12h14"/>',
      sample:  '<path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/>',
      chat:    '<path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8A8.5 8.5 0 1 1 21 11.5z"/>',
      doc:     '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8"/>',
      search:  '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>'
    };
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (paths[name] || '') + '</svg>';
  }

  /* ── Question tree (mirrors Obsidian spec §The flow) ────────────── */
  var STEPS = [
    {
      id: 'q1_types',
      type: 'multi',
      title: "What are you shopping for?",
      help: "Select all that apply.",
      icon: 'pull',
      options: [
        { value: 'knobs',           label: 'Knobs',                  icon: 'knob'  },
        { value: 'pulls',           label: 'Pulls & Handles',        icon: 'pull'  },
        { value: 'appliance-pulls', label: 'Appliance Pulls',        icon: 'pull'  },
        { value: 'hinges',          label: 'Hinges',                 icon: 'hinge' },
        { value: 'backplates',      label: 'Backplates',             icon: 'plate' },
        { value: 'accessories',     label: 'Cabinet Accessories',    icon: 'box'   }
        // 'other' / "Something else" removed 2026-06-12 (Dave): it mapped to NO
        // type filter, so results were just whatever led the collection (knobs
        // and pulls) — confusing, not useful. The results JS still tolerates
        // types=other in old shared URLs (treated as unfiltered).
      ],
      required: true
    },
    {
      id: 'q2_style',
      type: 'single',
      title: "Which style are you drawn to?",
      help: "Pick one. You can always broaden later.",
      options: [
        { value: 'modern-contemporary', label: 'Modern / Contemporary', sublabel: 'Clean lines, minimalist, sleek' },
        { value: 'transitional',        label: 'Transitional',          sublabel: 'A blend of classic and current' },
        { value: 'traditional',         label: 'Traditional',           sublabel: 'Ornate, classic, timeless' },
        { value: '',                    label: 'Show me everything',    sublabel: "I don't want to narrow by style", icon: 'none' }
      ],
      required: true
    },
    {
      id: 'q3_finish',
      type: 'single',
      title: "Any preferred finish?",
      help: "Pick one — or no preference if you're still deciding.",
      // Trimmed + reordered (popularity-first) 2026-06-19 (Dave): dropped
      // Steel/Aluminum/Silver (folded into Nickel), Brown (→Bronze), Clear
      // (→Colored) — tiny, redundant silver-tones. The results JS maps each
      // remaining class to an OR-group of Finish-Class facets so the folded
      // finishes still surface under the parent.
      options: [
        { value: '',         label: 'I have no preference',  icon: 'none' },
        { value: 'nickel',   label: 'Nickel',                swatch: 'nickel'   },
        { value: 'bronze',   label: 'Bronze',                swatch: 'bronze'   },
        { value: 'black',    label: 'Black',                 swatch: 'black'    },
        { value: 'chrome',   label: 'Chrome',                swatch: 'chrome'   },
        { value: 'gold',     label: 'Gold',                  swatch: 'gold'     },
        { value: 'brass',    label: 'Brass',                 swatch: 'brass'    },
        { value: 'pewter',   label: 'Pewter',                swatch: 'pewter'   },
        { value: 'copper',   label: 'Copper',                swatch: 'copper'   },
        { value: 'gray',     label: 'Gray',                  swatch: 'gray'     },
        { value: 'iron',     label: 'Iron',                  swatch: 'iron'     },
        { value: 'colored',  label: 'Colored',               swatch: 'colored'  }
        // White dropped 2026-06-19 (Dave): only ~20 white knobs / ~52 white pulls
        // (most "white" is switchplates/appliance), so it returned a near-empty
        // wizard. Iron is its own tile again (distinct rustic look) — no longer
        // folded into Black. Still 12 tiles (clean 4×3 grid).
      ],
      required: true
    },
    {
      id: 'q_budget',
      type: 'single',
      title: "What's your budget?",
      help: "We match to the middle of each category, not a fixed price — so a “mid-range” knob and a “mid-range” pull can cost different amounts, and both count as mid-range.",
      options: [
        { value: 'value',  label: 'Budget-friendly', sublabel: 'Best value in each category' },
        { value: 'mid',    label: 'Mid-range',       sublabel: 'A balance of quality and price' },
        { value: 'luxury', label: 'High-end',        sublabel: 'Premium & designer pieces' },
        { value: '',       label: 'No preference',   sublabel: 'Show me all price points', icon: 'none' }
      ],
      required: true
    },
    {
      id: 'q4_drill',
      type: 'single',
      title: "What size pulls are you looking for?",
      help: "Pulls are sized by the gap between their two screw holes — the “center-to-center” distance. If you're matching holes you already have, measure that gap. This is a pulls question only — knobs mount with a single screw, so it won't affect your knob matches. Not sure? Pick “I have no preference.”",
      condition: function (a) {
        var t = a.q1_types || [];
        return t.indexOf('pulls') >= 0 || t.indexOf('appliance-pulls') >= 0;
      },
      // Options adapt to the pull type: appliance pulls run much larger, so they
      // get their own specific size buckets (matched against the specific
      // details.drill_centers value, not the coarse "Larger than 8''" range).
      optionsFor: function (a) {
        var t = a.q1_types || [];
        var applianceOnly = t.indexOf('appliance-pulls') >= 0 && t.indexOf('pulls') < 0;
        if (applianceOnly) {
          return [
            { value: '',              label: 'I have no preference',  icon: 'none' },
            { value: 'appl-le8',      label: '8" or smaller',         sublabel: 'Compact appliance pulls' },
            { value: 'appl-10-12',    label: '10" – 12"',             sublabel: 'Most common — e.g. dishwasher' },
            { value: 'appl-13-15',    label: '13" – 15"' },
            { value: 'appl-16-18',    label: '16" – 18"',             sublabel: 'Large — e.g. refrigerator' },
            { value: 'appl-over-18',  label: 'Over 18"',              sublabel: 'Extra-large / double-door' }
          ];
        }
        return [
          { value: '',         label: 'I have no preference',           icon: 'none' },
          { value: 'under-3',  label: 'Under 3"',                       sublabel: 'Small pulls' },
          { value: '3-5',      label: '3" to 4.99" (76–127mm)',         sublabel: 'Most common range' },
          { value: '5-8',      label: '5" to 8" (128–203mm)',           sublabel: 'Medium-to-long pulls' },
          { value: 'over-8',   label: 'Over 8"',                        sublabel: 'Long bar pulls' }
        ];
      },
      required: true
    },
    {
      id: 'q5_speed',
      type: 'single',
      title: "How quickly do you need it?",
      options: [
        { value: 'fast',     label: 'I need it fast',           sublabel: 'In stock, ships in days', icon: 'fast' },
        { value: 'flexible', label: 'Show me everything',       sublabel: 'Best matches, any lead time', icon: 'flexible' },
        { value: 'custom',   label: 'Hand-crafted & custom',    sublabel: 'Artisan pieces, made to order', icon: 'custom' }
      ],
      required: true
    },
    {
      id: 'q6_trade',
      type: 'single',
      title: "Are you designing for your own home, or for a business or client?",
      options: [
        { value: 'home',  label: 'My own home',         sublabel: 'A personal project',                    icon: 'home'  },
        { value: 'trade', label: 'A business or client', sublabel: "I'm a designer, builder or trade pro",  icon: 'trade' }
      ],
      required: true
    },
    {
      id: 'q_interests',
      type: 'multi',
      title: "Last thing — anything you'd like help with?",
      help: "Optional. Check any that apply and we'll set it up on your results page.",
      options: [
        { value: 'samples',  label: 'Order samples',                   sublabel: 'Rebate when you buy',                 icon: 'sample' },
        { value: 'consult',  label: 'Free design consultation',        sublabel: 'A free 15-min call with Kayla',       icon: 'chat'   },
        { value: 'quote',    label: 'A quote for a large order',       sublabel: 'Bulk / trade pricing on a big order', icon: 'doc'    },
        { value: 'matching', label: 'Help matching existing hardware', sublabel: "Match what you've already got",       icon: 'search' }
      ],
      required: false
    }
    // q6_trade (home vs client) was replaced by q_interests 2026-06-19 (Dave): the
    // "Trade pricing" checkbox is the trade signal now, and the multi-select also
    // captures samples / consult / quote / matching intent to drive the results CTAs.
    // q7_consult (Kayla consult yes/no) REMOVED 2026-06-19 (Dave): asking
    // people to commit to a call BEFORE they've seen a single match is a sales
    // gate that costs completions. The design-session offer now lives on the
    // results page for EVERYONE — primary card for homeowners, compact secondary
    // line under the Trade Pros banner for trade pros.
    // trade_form step was removed earlier (2026-06-12); q6_trade is now the last
    // question and drives both the Trade Pros banner and the consult hierarchy
    // on the results page.
  ];

  /* ── State + URL serialization ──────────────────────────────────── */
  var state = {
    answers: {},     // { q1_types:[...], q2_style:'...', ... }
    currentStepIndex: 0,
    visitedStepIds: [] // for "back" button behavior with conditionals
  };

  /* ── Klaviyo lead capture (task #4) ─────────────────────────────────
     theme.liquid loads Klaviyo onsite.js (company TiKVCi, public id) via a
     deferred interaction/idle loader. window.klaviyo is an Array before the
     SDK loads and the SDK object after; .push([...]) works in BOTH states
     (the SDK keeps array-tuple compatibility), so we use it universally.
     No Admin token is ever exposed client-side. */
  function klaviyoPush(tuple) {
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(tuple);
      return true;
    } catch (e) { return false; }
  }

  function readUrlAnswers() {
    var p = new URLSearchParams(window.location.search);
    var a = {};
    var t = p.get('types'); if (t) a.q1_types = t.split(',').filter(Boolean);
    var v;
    v = p.get('style');   if (v != null) a.q2_style = v;
    v = p.get('finish');  if (v != null) a.q3_finish = v;
    v = p.get('budget');  if (v != null) a.q_budget = v;
    v = p.get('drill');   if (v != null) a.q4_drill = v;
    v = p.get('speed');   if (v) a.q5_speed = v;
    v = p.get('trade');   if (v) a.q6_trade = v;
    var iv = p.get('interests'); if (iv) a.q_interests = iv.split(',').filter(Boolean);
    return a;
  }

  function answersToParams(a) {
    var p = new URLSearchParams();
    if (a.q1_types && a.q1_types.length) p.set('types', a.q1_types.join(','));
    if (a.q2_style != null && a.q2_style !== undefined) p.set('style', a.q2_style);
    if (a.q3_finish != null && a.q3_finish !== undefined) p.set('finish', a.q3_finish);
    if (a.q_budget != null && a.q_budget !== undefined) p.set('budget', a.q_budget);
    if (a.q4_drill != null && a.q4_drill !== undefined) p.set('drill', a.q4_drill);
    if (a.q5_speed) p.set('speed', a.q5_speed);
    if (a.q6_trade) p.set('trade', a.q6_trade);
    if (a.q_interests && a.q_interests.length) p.set('interests', a.q_interests.join(','));
    return p;
  }

  var LS_KEY = 'knobs.wizard.v1';
  function persist() {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ answers: state.answers, currentStepIndex: state.currentStepIndex })); } catch (e) {}
    var p = answersToParams(state.answers);
    // Preserve theme-preview params across answer-driven URL rewrites so an
    // unpublished preview theme (and the ?view=wizard alternate template) survive
    // QA click-throughs. Both are absent on the live storefront, so this is a no-op there.
    var existing = new URLSearchParams(window.location.search);
    ['preview_theme_id', 'view'].forEach(function (k) { var val = existing.get(k); if (val) p.set(k, val); });
    var newUrl = window.location.pathname + (p.toString() ? '?' + p.toString() : '') + window.location.hash;
    try { window.history.replaceState(null, '', newUrl); } catch (e) {}
  }
  function restorePersisted() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }

  /* ── Step navigation (with conditionals) ────────────────────────── */
  function effectiveSteps() {
    return STEPS.filter(function (s) { return !s.condition || s.condition(state.answers); });
  }
  function currentStep() {
    return effectiveSteps()[state.currentStepIndex];
  }
  function totalSteps() {
    return effectiveSteps().length;
  }

  function setAnswer(stepId, value) {
    state.answers[stepId] = value;
    persist();
  }

  function validateStep(step) {
    var a = state.answers[step.id];
    if (!step.required) return true;
    if (step.type === 'multi') return Array.isArray(a) && a.length > 0;
    if (step.type === 'single') return a !== undefined; // empty string '' is valid (= no preference)
    if (step.type === 'form') {
      if (!a || typeof a !== 'object') return false;
      for (var i = 0; i < step.fields.length; i++) {
        var f = step.fields[i];
        if (f.required && !a[f.name]) return false;
      }
      return true;
    }
    return false;
  }

  /* GA4 funnel events via KWTrack (knobs-wizard-track.js); no-op if absent. */
  function track(name, params) {
    if (typeof window.KWTrack === 'function') window.KWTrack(name, params);
  }
  function answerString(v) {
    return Array.isArray(v) ? v.join(',') : (v == null ? '' : String(v));
  }

  function navigate(delta) {
    var steps = effectiveSteps();
    var cur = steps[state.currentStepIndex];
    if (delta > 0 && !validateStep(cur)) {
      flashError(cur);
      return;
    }
    if (delta > 0) {
      track('wizard_step_complete', {
        step_id: cur.id,
        step_index: state.currentStepIndex + 1,
        answer: answerString(state.answers[cur.id]).slice(0, 100)
      });
    }
    var nextIdx = state.currentStepIndex + delta;
    if (nextIdx < 0) return;
    if (nextIdx >= steps.length) { complete(); return; }
    state.currentStepIndex = nextIdx;
    persist();
    renderStep();
  }

  function complete() {
    persist();
    var a = state.answers;
    // Order attribution: a 90-day "hardware plan" record that knobs-journey.js
    // (site-wide) stamps onto the Shopify cart as attributes, so every later
    // order — samples today, the project order weeks later — says it came
    // after the wizard. Source is a field so a future advisor can reuse it.
    try {
      var flat = {};
      Object.keys(a).forEach(function (k) { var v = answerString(a[k]); if (v) flat[k.replace(/^q\d*_/, '')] = v.slice(0, 80); });
      localStorage.setItem('kw.plan.v1', JSON.stringify({ source: 'wizard', ts: Date.now(), answers: flat }));
    } catch (e) {}
    if (typeof window.KWUser === 'function') {
      window.KWUser({ wizard_completed: 'yes', wizard_completed_date: new Date().toISOString().slice(0, 10) });
    }
    track('wizard_complete', {
      types: answerString(a.q1_types), style: answerString(a.q2_style),
      finish: answerString(a.q3_finish), drill: answerString(a.q4_drill),
      speed: answerString(a.q5_speed), budget: answerString(a.q_budget),
      interests: answerString(a.q_interests), trade: answerString(a.q6_trade)
    });
    var p = answersToParams(state.answers);
    // Carry the preview theme through to the results page (no-op on live).
    var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) p.set('preview_theme_id', preview);
    window.location.href = resultsUrl + (p.toString() ? '?' + p.toString() : '');
  }

  /* ── Rendering ──────────────────────────────────────────────────── */
  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.indexOf('on') === 0 && typeof attrs[k] === 'function') n.addEventListener(k.slice(2), attrs[k]);
      else if (k === 'dataset') Object.keys(attrs[k]).forEach(function (d) { n.dataset[d] = attrs[k][d]; });
      else if (attrs[k] === true) n.setAttribute(k, '');
      else if (attrs[k] !== false && attrs[k] != null) n.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) {
      if (c == null) return;
      n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return n;
  }

  function renderCard(step, opt, selected) {
    var visual;
    if (opt.swatch) {
      var bg = FINISH_SWATCH[opt.swatch] || '#888';
      var style = (bg.indexOf('gradient') >= 0) ? ('background:' + bg) : ('background:' + bg);
      var borderHint = (opt.swatch === 'white') ? ' kw-card__swatch--bordered' : '';
      visual = el('span', { class: 'kw-card__swatch' + borderHint, style: style, 'aria-hidden': 'true' });
    } else if (opt.icon) {
      visual = el('span', { class: 'kw-card__icon', 'aria-hidden': 'true', html: svg(opt.icon) });
    }
    var labelEl = el('span', { class: 'kw-card__label' }, [opt.label]);
    var sublabelEl = opt.sublabel ? el('span', { class: 'kw-card__sublabel' }, [opt.sublabel]) : null;

    var role = (step.type === 'multi') ? 'checkbox' : 'radio';
    var checked = !!selected;
    var card = el('button', {
      type: 'button',
      class: 'kw-card' + (checked ? ' is-selected' : ''),
      role: role,
      'aria-checked': checked ? 'true' : 'false',
      tabindex: '0',
      dataset: { value: opt.value === '' ? '__empty__' : opt.value }
    }, [visual, labelEl, sublabelEl]);
    return card;
  }

  function renderSingle(step) {
    var current = state.answers[step.id];
    var options = step.optionsFor ? step.optionsFor(state.answers) : step.options;
    var grid = el('div', { class: 'kw-grid kw-grid--' + (options.length > 8 ? 'tight' : 'comfy'), role: 'radiogroup', 'aria-labelledby': 'kw-step-title' });
    options.forEach(function (opt) {
      var isSel = (current !== undefined && current === opt.value);
      grid.appendChild(renderCard(step, opt, isSel));
    });
    grid.addEventListener('click', function (e) {
      var card = e.target.closest('.kw-card');
      if (!card) return;
      var raw = card.dataset.value;
      var v = raw === '__empty__' ? '' : raw;
      setAnswer(step.id, v);
      // Update DOM
      grid.querySelectorAll('.kw-card').forEach(function (c) {
        var sel = c === card;
        c.classList.toggle('is-selected', sel);
        c.setAttribute('aria-checked', sel ? 'true' : 'false');
      });
      announce("Selected: " + (card.querySelector('.kw-card__label').textContent));
    });
    return grid;
  }

  function renderMulti(step) {
    var current = Array.isArray(state.answers[step.id]) ? state.answers[step.id].slice() : [];
    var grid = el('div', { class: 'kw-grid kw-grid--comfy kw-grid--multi', role: 'group', 'aria-labelledby': 'kw-step-title' });
    step.options.forEach(function (opt) {
      var isSel = current.indexOf(opt.value) >= 0;
      grid.appendChild(renderCard(step, opt, isSel));
    });
    grid.addEventListener('click', function (e) {
      var card = e.target.closest('.kw-card');
      if (!card) return;
      var raw = card.dataset.value;
      var v = raw === '__empty__' ? '' : raw;
      var arr = Array.isArray(state.answers[step.id]) ? state.answers[step.id].slice() : [];
      var idx = arr.indexOf(v);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(v);
      setAnswer(step.id, arr);
      var nowSel = arr.indexOf(v) >= 0;
      card.classList.toggle('is-selected', nowSel);
      card.setAttribute('aria-checked', nowSel ? 'true' : 'false');
      announce((nowSel ? "Added" : "Removed") + ": " + card.querySelector('.kw-card__label').textContent);
    });
    return grid;
  }

  function renderForm(step) {
    var current = (state.answers[step.id] && typeof state.answers[step.id] === 'object') ? state.answers[step.id] : {};
    var form = el('form', { class: 'kw-form', novalidate: true });
    step.fields.forEach(function (f) {
      var id = 'kw-f-' + f.name;
      var label = el('label', { class: 'kw-form__label', for: id }, [
        f.label,
        f.required ? el('span', { class: 'kw-form__req', 'aria-label': 'required' }, [' *']) : null
      ]);
      var input;
      if (f.type === 'select') {
        input = el('select', { id: id, name: f.name, class: 'kw-form__select', required: !!f.required }, [
          el('option', { value: '' }, ['— Select —'])
        ].concat(f.options.map(function (o) {
          var sel = current[f.name] === o;
          var attrs = { value: o };
          if (sel) attrs.selected = true;
          return el('option', attrs, [o]);
        })));
      } else if (f.type === 'textarea') {
        input = el('textarea', { id: id, name: f.name, class: 'kw-form__textarea', rows: '3', required: !!f.required }, [current[f.name] || '']);
      } else {
        input = el('input', { id: id, name: f.name, type: f.type, class: 'kw-form__input', required: !!f.required, autocomplete: f.autocomplete || 'off', value: current[f.name] || '' });
      }
      var row = el('div', { class: 'kw-form__row' }, [label, input]);
      form.appendChild(row);
    });
    form.addEventListener('input', function () {
      var data = {};
      step.fields.forEach(function (f) {
        var n = form.elements[f.name];
        if (n) data[f.name] = (n.value || '').trim();
      });
      setAnswer(step.id, data);
    });
    return form;
  }

  function renderStep() {
    var step = currentStep();
    if (!step) return;

    if (loadingEl && loadingEl.parentNode) loadingEl.parentNode.removeChild(loadingEl);
    hostEl.innerHTML = '';

    var titleId = 'kw-step-title';
    var titleEl = el('h2', { id: titleId, class: 'kw-step__title' }, [step.title]);
    var helpEl = step.help ? el('p', { class: 'kw-step__help' }, [step.help]) : null;

    var body;
    if (step.type === 'single') body = renderSingle(step);
    else if (step.type === 'multi') body = renderMulti(step);
    else if (step.type === 'form') body = renderForm(step);

    var errorEl = el('p', { class: 'kw-step__error', role: 'alert', 'data-error': true });

    var idx = state.currentStepIndex;
    var total = totalSteps();
    var navEl = el('div', { class: 'kw-nav' }, [
      el('button', {
        type: 'button',
        class: 'kw-btn kw-btn--ghost',
        disabled: idx === 0 ? true : false,
        onclick: function () { navigate(-1); }
      }, ['← Back']),
      el('span', { class: 'kw-nav__step' }, ['Step ' + (idx + 1) + ' of ' + total]),
      el('button', {
        type: 'button',
        class: 'kw-btn kw-btn--primary',
        onclick: function () { navigate(1); }
      }, [idx === total - 1 ? 'Show my matches →' : 'Next →'])
    ]);

    var stepEl = el('div', { class: 'kw-step kw-step--' + step.type, dataset: { stepId: step.id } }, [titleEl, helpEl, body, errorEl, navEl]);
    hostEl.appendChild(stepEl);

    // Progress
    var pct = Math.round(((idx + 1) / total) * 100);
    if (progressBarEl) progressBarEl.style.width = pct + '%';
    if (progressEl) progressEl.setAttribute('aria-valuenow', String(pct));

    // Focus management — move focus to the heading so screen readers announce.
    // preventScroll:true is REQUIRED — without it, focusing the step heading on the
    // initial render scrolls the page down to the quiz, skipping the landing hero
    // (the "doesn't land at the top" bug). The step is already in view on Next/Back.
    titleEl.setAttribute('tabindex', '-1');
    try { titleEl.focus({ preventScroll: true }); } catch (e) { titleEl.focus(); }
  }

  function flashError(step) {
    var err = hostEl.querySelector('[data-error]');
    if (!err) return;
    var msg = (step.type === 'multi') ? 'Please pick at least one option.'
      : (step.type === 'form') ? 'Please fill in the required fields.'
      : 'Please choose an option.';
    err.textContent = msg;
    err.classList.add('is-visible');
    setTimeout(function () { err.classList.remove('is-visible'); }, 2400);
    announce(msg);
  }

  /* ── ARIA live announcer ────────────────────────────────────────── */
  var liveRegion = null;
  function ensureLiveRegion() {
    if (liveRegion) return;
    liveRegion = el('div', {
      class: 'kw-sr-only',
      'aria-live': 'polite',
      'aria-atomic': 'true'
    });
    root.appendChild(liveRegion);
  }
  function announce(msg) {
    ensureLiveRegion();
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = msg; }, 30);
  }

  /* ── Keyboard navigation ────────────────────────────────────────── */
  hostEl.addEventListener('keydown', function (e) {
    var key = e.key;
    if (key === 'Enter' && e.target.classList && e.target.classList.contains('kw-card')) {
      e.preventDefault();
      e.target.click();
      return;
    }
    if (key === ' ' && e.target.classList && e.target.classList.contains('kw-card')) {
      e.preventDefault();
      e.target.click();
      return;
    }
    if (key === 'ArrowRight' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowUp') {
      var cards = Array.prototype.slice.call(hostEl.querySelectorAll('.kw-card'));
      if (cards.length && cards.indexOf(e.target) >= 0) {
        e.preventDefault();
        var i = cards.indexOf(e.target);
        var n = (key === 'ArrowRight' || key === 'ArrowDown') ? i + 1 : i - 1;
        if (n < 0) n = cards.length - 1;
        if (n >= cards.length) n = 0;
        cards[n].focus();
      }
    }
  });

  /* ── Init ───────────────────────────────────────────────────────── */
  function init() {
    var urlAnswers = readUrlAnswers();

    if (Object.keys(urlAnswers).length) {
      // A shared / resumed link carries the answers in the URL — honor them.
      state.answers = urlAnswers;
    } else {
      // Fresh visit: start BLANK. We deliberately do NOT replay localStorage from
      // a past run — that was pre-selecting the visitor's previous answers (the
      // "why is Pewter already picked?" bug). Only the finish defaults to "no
      // preference" so the visitor isn't nudged toward a finish they didn't choose.
      state.answers = { q3_finish: '' };
      track('wizard_start', {});
    }
    state.currentStepIndex = 0;
    renderStep();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // TODO (task #6): On wizard completion, push all quiz answers as Klaviyo
  //   profile properties + fire a wizard_completed event (non-trade path too).
})();
